import moment from 'moment-timezone';

// Shared date-copy helpers for the teacher classwork list rows (assignments, skill paths). Kept
// feature-neutral: callers pass plain date strings, not a feature's GraphQL row type.

// "Due Tomorrow" reads relatively; far dates and ranges fall back to "MMM D".
const DUE_DATE_FORMATS = {
  sameDay: '[Today]',
  nextDay: '[Tomorrow]',
  nextWeek: 'MMM D',
  lastDay: '[Yesterday]',
  lastWeek: 'MMM D',
  sameElse: 'MMM D'
};

export const formatDateTime = (date: string) => moment(date).format('MMM D, h:mm A');
export const formatEditedDate = (date: string) => moment(date).format('MMM D, YYYY');
const formatDateShort = (date: string) => moment(date).format('MMM D');
const formatDueDate = (date: string) => moment(date).calendar(null, DUE_DATE_FORMATS);

// Due/start dates span the educator's in-scope (class, student) pairings, so cross-class/-recipient
// rows show a min–max range and single ones collapse to one date.
export const formatDateRange = (
  min: string,
  max: string | null | undefined,
  formatSingle: (date: string) => string
): string =>
  max && max !== min ? `${formatDateShort(min)} - ${formatDateShort(max)}` : formatSingle(min);

// Exact due date/time + year + local-tz abbr.
const formatFullDueDate = (date: string): string => {
  const parsed = moment(date);
  const tzAbbr = moment.tz.zone(moment.tz.guess())?.abbr(parsed.valueOf()) ?? '';
  return `${parsed.format('MMMM Do, YYYY h:mma')} ${tzAbbr}`.trim();
};

export interface DueDateRange {
  minDueDate?: string | null;
  maxDueDate?: string | null;
}

// A row with no in-scope (class, student) pairings (e.g. every class archived or unenrolled) has
// null aggregates — show no date rather than the default settings date, which can match no class.
// `exact` (on hover/focus) reveals the full date/time instead of the relative "Due MMM D".
export const getDueCopy = ({minDueDate, maxDueDate}: DueDateRange, exact = false): string => {
  if (!minDueDate) {
    return '';
  }
  if (exact) {
    return maxDueDate && maxDueDate !== minDueDate
      ? `Due ${formatFullDueDate(minDueDate)} - ${formatFullDueDate(maxDueDate)}`
      : `Due ${formatFullDueDate(minDueDate)}`;
  }
  return `Due ${formatDateRange(minDueDate, maxDueDate, formatDueDate)}`;
};
